import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Camera,
    Tv,
    UserCheck,
    Sparkles,
    Zap,
    Clapperboard,
    Layers,
    Shield,
    Sliders,
    Expand,
    Eye,
    Image as ImageIcon
} from 'lucide-react';

const celebrityServices = [
    { id: '01', icon: Clapperboard, title: 'Theatrical Key Art Campaigns', desc: 'Engineering iconic, high-impact imagery engineered specifically to anchor worldwide theatrical, streaming, and home-entertainment launches.' },
    { id: '02', icon: UserCheck, title: 'A-List Editorial Press Kits', desc: 'High-concept, striking talent portraits custom-built for high-fashion spreads, talk show profiles, and global media junket assets.' },
    { id: '03', icon: Zap, title: 'Character Teaser Portfolios', desc: 'Crafting moody, high-contrast, character-driven lighting setups that telegraph a protagonist\'s or villain\'s deep psychological narrative.' },
    { id: '04', icon: Tv, title: 'Streaming Carousel Mastery', desc: 'Designing high-visibility tile assets mathematically optimized for click-through performance across major streaming platforms.' },
    { id: '05', icon: Sparkles, title: 'Red Carpet & Junket Pop-Ups', desc: 'Deploying rapid, ultra-flattering studio lighting modules backstage at massive awards shows and exclusive studio premieres.' },
    { id: '06', icon: Layers, title: 'High-Bit Dynamic Compositing', desc: 'Capturing precise background plates and elements with matching focal lengths for intricate, multi-talent Hollywood assemblies.' },
];

const studioAssetsServices = [
    { id: '01', icon: Sliders, title: 'Billboard-Scale Asset Mastering', desc: 'Delivering uncompressed, native medium-format digital files ready for flawless rendering onto sky-high city banners.' },
    { id: '02', icon: Shield, title: 'Strict Discretion & NDA Workflows', desc: 'White-glove data management systems built to fully isolate early production assets from leaks or premature public exposure.' },
    { id: '03', icon: Expand, title: 'VFX Green Plate Harmonization', desc: 'Aligning studio strobe axes precisely with post-production CGI environment metrics to guarantee seamless visual melting.' },
    { id: '04', icon: Eye, title: 'Publicity & PR Package Pre-Renders', desc: 'Rapid turnaround of pristine talent stills customized for instant pick-up by major trade publications and syndicates.' },
    { id: '05', icon: ImageIcon, title: 'Collector-Edition Steelbook Covers', desc: 'Curating stylized, artistic alternative framing layouts for limited physical releases, box sets, and merchandise.' },
];

const keyArtDeliverables = [
    { id: '01', icon: Shield, title: 'Ironclad Studio Content Security', desc: 'Asset isolation protocols utilizing air-gapped internal servers, encrypted transfers, and watermarked talent preview portals.' },
    { id: '02', icon: Sliders, title: '100MP Medium Format Captures', desc: 'Utilizing phase-one digital backs to capture infinite texture, facial micro-details, and incredible cropping latitude.' },
    { id: '03', icon: Zap, title: 'High-Speed Kinetic Syncing', desc: 'Freezing hyper-fast action, glass breaks, explosions, or intense martial-arts stunts with flash durations up to 1/10,000s.' },
    { id: '04', icon: Layers, title: 'Negative Space Graphic Inclusions', desc: 'Intentionally framing captures to accommodate clean typography baselines, billing blocks, critics\' quotes, and studio logos.' },
    { id: '05', icon: UserCheck, title: 'Elite Grooming & Wardrobe Alignment', desc: 'Synchronizing lighting temperatures instantly with elite key-stylists, glam squads, and prosthetics departments.' },
    { id: '06', icon: Clapperboard, title: 'Cinematic Ambient Emulation', desc: 'Recreating intricate onset lighting states—from neon dystopian futures to high-noon classic Western dust storms.' },
    { id: '07', icon: Expand, title: 'Multi-Aspect Responsive Layouts', desc: 'Pre-framing configurations built to slide beautifully into ultra-wide theater banners, square tiles, or vertical mobile feeds.' },
    { id: '08', icon: Tv, title: 'Rapid-Response EPK Asset Turning', desc: 'Same-night delivery workflows mapping highly polished press selections directly to international distribution pipelines.' },
    { id: '09', icon: ImageIcon, title: 'Native 16-Bit Layered TIFF Delivery', desc: 'Providing raw, perfectly isolated, multi-layered file architectures to simplify complex editorial adjustments by key art agencies.' },
];

const CelebrityAndMovieMedia = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.65;
        const speedRight = 0.45;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            <Helmet>
                <title>Celebrity Photography & Movie Poster Design in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="KhultStudios offers professional celebrity photography, movie poster design, film publicity photography, actor branding, promotional campaigns, first-look posters, teaser posters, and entertainment marketing services in Hyderabad."
                />

                <meta
                    name="keywords"
                    content="Celebrity Photography Hyderabad, Movie Poster Design Hyderabad, Film Poster Design, Movie Publicity Photography, Celebrity Photoshoot, Actor Branding, Actress Portfolio, Film Promotion, First Look Poster, Teaser Poster Design, Entertainment Photography, Tollywood Photography, Film Marketing, Movie Promotional Design, Cinema Poster Designer, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/celebrity-and-movie-posters"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/celebrity-and-movie-posters"
                />
                <meta
                    property="og:title"
                    content="Celebrity Photography & Movie Poster Design | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Professional celebrity photography, cinematic movie posters, first-look posters, promotional campaigns, and film branding services in Hyderabad."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/celebrity-movie-posters-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Celebrity Photography & Movie Poster Design | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Premium celebrity photography, movie poster design, film publicity, and entertainment branding in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/celebrity-movie-posters-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/key_art_reel.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Celebrity Portraiture & Key Art Production
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Immersive Stills That Sell The Cinema
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Before the first ticket is torn or a stream button is clicked, an audience must be captured. True theatrical key art isn't just a photograph—it's a masterfully orchestrated, hyper-scale visual summary that commands attention on global screens and massive city billboards.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we engineer cinematic imagery under tight studio conditions. Blending medium-format detail arrays, precise VFX plate mapping, and white-glove security practices, we deliver uncompromised assets that amplify A-list talent and sell epic stories.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Campaigns
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1618336753974-aae8e04506aa?q=80&w=500" alt="Moody Cyberpunk Character Portrait High Contrast" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=500" alt="A List Actor Studio Publicity Session" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1534447677768-be436bb09401?q=80&w=500" alt="Epic Movie Poster Landscape Dynamic Plate" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1509281373149-e957c6296406?q=80&w=500" alt="High Fashion Celebrity Magazine Cover Capture" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1618336753974-aae8e04506aa?q=80&w=500" alt="Moody Cyberpunk Character Portrait High Contrast Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=500" alt="A List Actor Studio Publicity Session Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1534447677768-be436bb09401?q=80&w=500" alt="Epic Movie Poster Landscape Dynamic Plate Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1509281373149-e957c6296406?q=80&w=500" alt="High Fashion Celebrity Magazine Cover Capture Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/pre-wedding-and-wedding-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/events-and-celebrations-photography.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/Arts.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/modelling-and-acting-photography.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/home/movie-posters.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/pre-wedding-and-wedding-photography.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/products-and-brands-photography.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/maternity-and-baby-photography1.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* COMMERCIAL ENTERTAINMENT SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">🎬 High-Concept Entertainment Visual Systems</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        In an attention-scarce landscape, promotional stills must carry theatrical scale. Our premium production modules are built to seamlessly integrate into demanding studio marketing ecosystems.
                    </p>
                    <div className="row g-4 mt-2">
                        {celebrityServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 0 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STUDIO LOGISTICS SECTION */}
            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">🏢 Studio-Grade Infrastructure & Scale Management</h2>
                        <p className="text-light-gray mt-3 max-w-2xl mx-auto">
                            Managing high-stakes campaigns requires precision logistics. From strict metadata control and closed security practices to billboard output configurations, we safeguard the commercial value of your IP.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {studioAssetsServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* KEY ART TECHNICAL SPECIFICATION MATRIX */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">⚙️ Technical Specifications & Campaign Deliverables</h2>
                    <div className="row g-4">
                        {keyArtDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CelebrityAndMovieMedia;